<template>
    <div class="content-wrapper">
        <!-- Page Loader -->
        <div class="vld-parent">
            <loading :active.sync="isLoading" :is-full-page="fullPage"></loading>
        </div>

        <breadcrumb :page="$t('form.setting.google2fa')"></breadcrumb>

        <div class="content">
            <div class="container-fluid">

                <b-alert v-model="alert" :variant="alertClass" dismissible fade>{{alertMessage}}</b-alert>

                <b-row>
                    <b-col md="12">
                        <b-form-group>
                            <b-form-checkbox v-model="selected" @change="save2faSettings" value="1" unchecked-value="0">
                                {{$t('form.setting.exclude2FA')}}
                            </b-form-checkbox>
                        </b-form-group>
                    </b-col>

                    <b-col md="7">
                        <form action="#" @submit.prevent="addIp">
                            <div class="card">
                                <div class="card-header bg-light">
                                    <div class="card-title">{{$t('form.setting.ipAdd')}}</div>
                                    <b-button variant="primary" type="submit" class="float-right">
                                        {{$t('button.add')}}
                                    </b-button>
                                </div>

                                <div class="card-body">
                                    <div class="form-group">
                                        <label>{{$t('form.setting.ip')}}</label>
                                        <input type="text" class="form-control" v-model.trim="$v.ip.$model"
                                            :class="{ 'is-invalid': $v.ip.$error }"
                                            :placeholder="$t('form.setting.subnet')">
                                        <div class="invalid-feedback" v-if="!$v.ip.required">
                                            {{$t('form.validation.required')}}</div>
                                        <div class="invalid-feedback" v-if="!$v.ip.cidr">
                                            {{$t('form.validation.subnet')}}</div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div class="card">
                            <div class="card-header bg-light">
                                <div class="card-title">{{$t('form.setting.userList')}}</div>
                            </div>

                            <div class="card-body table-responsive p-0">
                                <table class="table table-hover" v-if="users.length">
                                    <thead>
                                        <th>{{$t('form.setting.name')}}</th>
                                        <th>{{$t('form.setting.email')}}</th>
                                        <th>{{$t('form.setting.google2FAMode')}}</th>
                                        <th>{{$t('form.setting.status')}}</th>
                                        <th class="text-right">{{$t('form.setting.action')}}</th>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(user,index) in users" :key="index">
                                            <td>{{user.name}}</td>
                                            <td>{{user.email}}</td>
                                            <td>{{user.google2fa_mode}}</td>
                                            <td>
                                                <b-badge variant="success"
                                                    v-if="user.password_security.google2fa_enable">
                                                    {{$t('form.setting.enabled')}}</b-badge>
                                                <b-badge v-else>{{$t('form.setting.disabled')}}</b-badge>
                                            </td>
                                            <td class="text-right">
                                                <b-button variant="info" size="sm"
                                                    @click="update2FAMode(index, 'enforced')">{{$t('button.enforce')}}
                                                </b-button>
                                                <b-button variant="warning" size="sm"
                                                    @click="update2FAMode(index, 'optional')">{{$t('button.optional')}}
                                                </b-button>
                                                <b-button variant="danger" size="sm"
                                                    @click="update2FAMode(index, 'reset')">{{$t('button.reset')}}
                                                </b-button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <p class="p-3 m-0" v-else>{{$t('noRecord')}}</p>
                            </div>

                            <pagination v-if="userPagination.last_page > 1" :pagination="userPagination" :offset="5"
                                @paginate="paginate('user')">
                            </pagination>
                        </div>
                    </b-col>

                    <b-col md="5">
                        <div class="card">
                            <div class="card-header bg-light">
                                <div class="card-title">{{$t('form.setting.ipList')}}</div>
                            </div>

                            <div class="card-body table-responsive p-0">
                                <table class="table table-hover" v-if="ips.length">
                                    <thead>
                                        <tr>
                                            <th>{{$t('form.setting.ip')}}</th>
                                            <th class="text-right">{{$t('form.setting.action')}}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(ip, index) in ips" :key="index">
                                            <td>{{ip.ip}}</td>
                                            <td class="text-right">
                                                <button class="btn btn-danger btn btn-sm" @click="deleteIp(index)"
                                                    :title="$t('form.setting.deleteIP')"
                                                    v-if="permissions.deleteSettingsIps">
                                                    <span class="fa fa-trash"></span>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <p class="p-3 m-0" v-else>{{$t('noRecord')}}</p>
                            </div>

                            <pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="5"
                                @paginate="paginate()">
                            </pagination>
                        </div>
                    </b-col>
                </b-row>
            </div>
        </div>
    </div>
</template>

<script>
    import {
        helpers,
        required
    } from "vuelidate/lib/validators";

    const cidr = helpers.regex('cidr', /^([0-9]{1,3}\.){3}[0-9]{1,3}(\/([0-9]|[1-2][0-9]|3[0-2]))?$/);

    export default {
        props: ['permissions', 'datetimeFormat'],

        data() {
            return {
                alert: false,
                alertClass: '',
                alertMessage: '',

                isLoading: false,
                fullPage: true,

                pagination: {
                    current_page: 1
                },

                userPagination: {
                    current_page: 1
                },

                ip: null,
                selected: 0,

                ips: [],
                users: []
            }
        },

        validations: {
            ip: {
                required,
                cidr
            }
        },

        mounted() {
            if (!this.permissions.viewSettingsGoogle2fa) {
                return this.$router.push('/');
            }

            this.get2faSettings();
            this.getIps();
            this.getUsers();
        },

        methods: {

            update2FAMode(index, action) {
                // check same action request call
                if (action == this.users[index].google2fa_mode) {
                    return;
                }

                this.isLoading = true;

                axios
                    .put(`/api/users/${this.users[index].id}/google-2fa`, {
                        mode: action
                    })
                    .then(response => {
                        // console.log(response.data);
                        if (response.data.success) {
                            this.users[index].google2fa_mode = response.data.data.google2fa_mode;

                            if (!response.data.data.password_security) {
                                this.users.splice(index, 1);
                            }

                        }

                        this.isLoading = false;
                    })
                    .catch(error => {
                        // console.log(error.response);
                        this.isLoading = false;
                    })
            },

            getUsers() {
                this.isLoading = true;

                axios
                    .get('/api/users?2fa&page=' + this.userPagination.current_page)
                    .then((response) => {
                        this.users = response.data.data.data;
                        this.userPagination = response.data.pagination;

                        this.isLoading = false;
                    })
                    .catch(error => {
                        console.log(error.response)

                        this.isLoading = false;
                    });
            },

            save2faSettings(value) {
                this.isLoading = true;

                axios
                    .post("/api/settings?name=google2fa-exclude", {
                        value: value
                    })
                    .then(response => {
                        this.isLoading = false;
                    })
                    .catch(error => {
                        this.isLoading = false;
                        console.log('error updating google 2fa ips exclude settings.');
                    });
            },

            get2faSettings() {
                axios
                    .get("/api/settings?name=google2fa-exclude")
                    .then(response => {
                        if (response.data) this.selected = response.data;
                    })
                    .catch(error => {
                        console.log('error fetching google 2fa ips exclude settings.');
                    });
            },

            // Get Ips
            getIps() {
                this.isLoading = true;

                axios
                    .get('/api/ips?type=google2fa&page=' + this.pagination.current_page)
                    .then(response => {
                        this.ips = response.data.data.data;
                        this.pagination = response.data.pagination;

                        this.isLoading = false;
                    })
                    .catch(error => {
                        console.log('error fetching ips.');

                        this.isLoading = false;
                    });
            },

            // Pagination
            paginate(action = null) {

                if (action) {
                    this.getUsers();
                    return;
                }

                this.getIps();
            },

            addIp() {

                this.$v.ip.$touch();

                if (this.$v.ip.$invalid) {
                    this.showAlert(this.$t('form.validation.error'), 'danger');
                    return;
                }

                this.isLoading = true;

                axios
                    .post('/api/ips', {
                        ip: this.ip,
                        type: 'google2fa'
                    })
                    .then(response => {
                        this.ip = null;
                        this.$v.ip.$reset();

                        this.showAlert(response.data.message);
                        this.ips.unshift(response.data.data);

                        this.isLoading = false;
                    })
                    .catch(error => {

                        if (error.response.status == 422 && error.response.data.error) {
                            this.showAlert(error.response.data.message, 'danger');
                        }

                        if (error.response.data && error.response.data.errors) {
                            let errors = error.response.data.errors;

                            if (errors.ip) {
                                this.showAlert(errors.ip[0], 'danger');
                            }
                        }

                        this.isLoading = false;
                    });
            },

            deleteIp(index) {
                let conf = confirm(this.$t('form.validation.delete'));

                if (conf === true) {
                    this.isLoading = true;

                    axios
                        .delete(`/api/ips/${this.ips[index].id}`)
                        .then(response => {
                            if (response.status == 200) {
                                this.showAlert(response.data.message);
                                this.ips.splice(index, 1);

                                this.isLoading = false;
                            }
                        })
                        .catch(error => {
                            this.isLoading = false;

                            console.log('error');
                        });
                }
            },

            /**
             * Show alert
             */
            showAlert(message, cls = 'success') {
                this.alertMessage = message;
                this.alertClass = cls;
                this.alert = true;
            },

        }
    }

</script>
